import { useState } from "react";
import Barcode from "react-barcode";
import { Check, ChevronDown, ChevronUp, Copy } from "lucide-react";
import PaymentLayout from "../layouts/PaymentLinkLayouts";
import { stripLinkquLabel } from "../utils/paymentMethodDisplay";
import useTranslation from "../hooks/useTranslation";

export default function PaymentInstructionOffline({
  onCheckStatus,
  paymentCode,
  merchantName,
  storeName,
  storeCode,
  onBack,
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isInstructionOpen, setIsInstructionOpen] = useState(false);
  const displayStoreName = stripLinkquLabel(storeName) || "offline store";
  const normalizedStoreCode = String(storeCode || "").toUpperCase();

  const instructions = normalizedStoreCode.includes("INDOMARET")
    ? t("paymentInstructionOffline.indomaretInstructions")
    : normalizedStoreCode.includes("ALFAMART")
      ? t("paymentInstructionOffline.alfamartInstructions")
      : [
          t("paymentInstructionOffline.genericInstruction1", { storeName: displayStoreName }),
          t("paymentInstructionOffline.genericInstruction2"),
          t("paymentInstructionOffline.genericInstruction3"),
          t("paymentInstructionOffline.genericInstruction4"),
          t("paymentInstructionOffline.genericInstruction5", { storeName: displayStoreName }),
        ];

  const handleCopy = async () => {
    if (!paymentCode) return;

    try {
      await navigator.clipboard.writeText(paymentCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <PaymentLayout unframedContent onBack={onBack}>
      <div className="rounded-2xl bg-white p-5 shadow-sm md:p-7">
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-6 md:px-7">
          <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(220px,260px)] md:items-center">
            <div className="order-2 md:order-1">
              <div>
                <p className="text-sm font-normal text-gray-500">{t("paymentInstructionOffline.paymentCode")}</p>
                <div className="mt-1 flex items-center gap-1.5 max-w-none md:max-w-44">
                  <span className="break-all text-base font-semibold leading-5 text-gray-900">
                    {paymentCode || "-"}
                  </span>
                  {paymentCode && (
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="shrink-0 text-singaHijauPrimary transition hover:text-singaHijauPrimary/80"
                      aria-label="Copy payment code"
                    >
                      {copied ? (
                        <Check size={15} strokeWidth={2.4} aria-hidden />
                      ) : (
                        <Copy size={15} strokeWidth={2.4} aria-hidden />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <p className="text-sm font-normal text-gray-500">{t("paymentInstructionOffline.payToMerchant")}</p>
                <p className="mt-1 text-base font-semibold leading-5 text-gray-900">{merchantName || "-"}</p>
              </div>
            </div>

            <div className="order-1 flex flex-col items-center justify-center md:order-2">
              <p className="mb-2 text-sm font-normal text-gray-900">{t("paymentInstructionOffline.showToCashier")}</p>
              <div className="flex w-full max-w-[210px] justify-center md:max-w-[230px] [&_svg]:h-auto [&_svg]:max-w-full">
                <Barcode
                  value={paymentCode || "-"}
                  width={1.15}
                  height={72}
                  margin={0}
                  displayValue={false}
                  renderer="svg"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setIsInstructionOpen((value) => !value)}
            className="flex h-14 w-full items-center justify-between gap-4 px-4 text-left"
          >
            <span className="text-sm font-semibold text-gray-900">{t("paymentInstructionOffline.howToPay")}</span>
            {isInstructionOpen ? (
              <ChevronUp size={18} className="shrink-0 text-gray-700" strokeWidth={2.5} aria-hidden />
            ) : (
              <ChevronDown size={18} className="shrink-0 text-gray-700" strokeWidth={2.5} aria-hidden />
            )}
          </button>
          {isInstructionOpen && (
            <div className="border-t border-gray-200 px-4 py-4">
              <ol className="list-decimal space-y-2 pl-5 text-sm font-normal leading-5 text-gray-600">
                {instructions.map((instruction) => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onCheckStatus}
          className="mt-4 flex h-12 w-full items-center justify-center rounded-xl border border-singaHijauPrimary bg-white text-sm font-semibold text-singaHijauPrimary transition hover:bg-green-50"
        >
          {t("common.checkPaymentStatus")}
        </button>
      </div>
    </PaymentLayout>
  );
}
